import { useState } from "react";
import { Maximize2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DocumentPreview } from "@/components/print/DocumentPreview";
import { Button } from "@akuna/ui";
import { cn } from "@/lib/utils";
import { PrescriptionPreviewDialog } from "./PrescriptionPreviewDialog";
import { usePrescriptionDraftPreview } from "./PrescriptionDraftContext";

export function ViaTabs({ names, active, onChange, className }: { names: string[]; active: number; onChange: (i: number) => void; className?: string }) {
  if (names.length <= 1) return null;
  return (
    <div role="tablist" className={cn("flex flex-wrap gap-1 px-1 text-xs", className)}>
      {names.map((name, index) => (
        <button
          key={name || index}
          type="button"
          role="tab"
          aria-selected={index === active}
          onClick={() => onChange(index)}
          className={cn(
            "rounded-full px-2.5 py-1 font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            index === active ? "bg-foreground text-background" : "bg-secondary text-foreground",
          )}
        >
          {name}
        </button>
      ))}
    </div>
  );
}

export function PrescriptionDraftPreviewCard({ className }: { className?: string }) {
  const { t } = useTranslation("prescriptions");
  const { html, viaNames, activeVia, setActiveVia, hasProvider } = usePrescriptionDraftPreview();
  const [zoomOpen, setZoomOpen] = useState(false);

  if (!hasProvider || !html) return null;

  return (
    <section
      aria-label={t("editor.preview.title")}
      className={cn("flex flex-col gap-2.5 rounded-card border border-border/60 bg-card px-3 pb-3 pt-3.5 shadow-soft-card lg:sticky lg:top-4", className)}
    >
      <div className="flex items-center gap-2 px-1">
        <h3 className="flex-1 text-base font-semibold">{t("editor.preview.title")}</h3>
        <Button type="button" variant="outline" size="sm" className="h-[34px] rounded-[9px] px-2.5 text-[12.5px] font-semibold" onClick={() => setZoomOpen(true)}>
          <Maximize2 className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
          {t("editor.preview.enlarge")}
        </Button>
      </div>
      <ViaTabs names={viaNames} active={activeVia} onChange={setActiveVia} />
      <button
        type="button"
        onClick={() => setZoomOpen(true)}
        aria-label={t("editor.preview.enlarge")}
        className="flex cursor-zoom-in justify-center rounded-[10px] bg-print-workspace py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <DocumentPreview html={html} title={t("editor.preview.title")} scale="fit" className="pointer-events-none" />
      </button>
      <PrescriptionPreviewDialog open={zoomOpen} onOpenChange={setZoomOpen} />
    </section>
  );
}
